// FILE: src/api.js
import axios from "axios";

// Create an Axios instance
const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "https://cashpro.striverhive.com", // Use environment variable for API URL
});

// Automatically attach the JWT token to all requests
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error("Request Error:", error);
    return Promise.reject(error);
  }
);

API.interceptors.response.use(
  (response) => response,
  (error) => {
    const { status, data } = error.response || {};
    // Log out if the token is expired or invalid
    if (
      status === 401 ||
      (status === 403 &&
        data &&
        data.message &&
        data.message.includes("expired"))
    ) {
      alert("Session expired. Please log in again.");
      localStorage.removeItem("token");
      window.location.href = "/auth";
    }
    return Promise.reject(error);
  }
);

// Helper function for error handling in API calls
const handleAPIError = (error) => {
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  return "An unexpected error occurred. Please try again later.";
};

// FETCH ACCOUNTS with search and pagination
export const fetchAccounts = async (search = "", page = 1) => {
  try {
    const response = await API.get(`/accounts`, {
      params: { search, page },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching accounts:", error);
    throw new Error(handleAPIError(error));
  }
};

// CREATE a new account
export const createAccount = async (accountData) => {
  try {
    const response = await API.post("/accounts/create", accountData);
    return response.data;
  } catch (error) {
    console.error("Error creating account:", error);
    throw new Error(handleAPIError(error));
  }
};

// FETCH TRANSACTIONS with optional type and pagination
export const fetchTransactions = async (type = "", page = 1) => {
  try {
    const response = await API.get(`/transactions`, {
      params: { type, page },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching transactions:", error);
    throw new Error(handleAPIError(error));
  }
};

// CREATE TRANSACTION
export const createTransaction = async (transactionData) => {
  try {
    const response = await API.post("/transactions/create", transactionData);
    return response.data;
  } catch (error) {
    console.error("Error creating transaction:", error);
    throw new Error(handleAPIError(error));
  }
};

// SHIFT API calls
export const createShift = async (shiftData) => {
  const res = await API.post("/shifts/create", shiftData);
  return res.data;
};

export const getShifts = async (params = {}) => {
  // e.g., params => { account, c2bStatus, startDate, endDate, search, page, limit }
  const res = await API.get("/shifts", { params });
  return res.data; // { data: [...], totalCount, currentPage, totalPages }
};

export const finalizeShifts = async (payload) => {
  // payload => { shiftsData: [{ shiftId, actualC2B, c2bNotes }, ...], actualC2BDate }
  const res = await API.put("/shifts/finalize", payload);
  return res.data;
};

export const getShiftById = async (shiftId) => {
  const res = await API.get(`/shifts/${shiftId}`);
  return res.data;
};

export const updateShift = async (shiftId, data) => {
  const res = await API.put(`/shifts/${shiftId}`, data);
  return res.data;
};

// USER API calls
export const getAllUsers = async () => {
  const res = await API.get("/users?limit=1000000");
  return res.data;
};

// *** ADVANCED REPORTS API calls ***

/**
 * Get Till Variance Report from the advanced reports module.
 * Expected query parameters: startDate, endDate, account (use "ALL" for all accounts)
 */
export const getTillVarianceReport = async (params = {}) => {
  try {
    const response = await API.get("/advance-reports/till-variance", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching till variance report:", error);
    throw new Error(handleAPIError(error));
  }
};

/**
 * Get C2B Variance Report from the advanced reports module.
 * Expected query parameters: startDate, endDate, account (use "ALL" for all accounts)
 */
export const getC2BVarianceReport = async (params = {}) => {
  try {
    const response = await API.get("/advance-reports/c2b-variance", { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching C2B variance report:", error);
    throw new Error(handleAPIError(error));
  }
};

export default API;
